import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Trash2, CheckCircle, Clock, Mail, Phone, Building2, MapPin, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const QuoteRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/admin/quote-requests`, { headers: getHeaders() }); setRequests(data); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/api/admin/quote-requests/${id}/status`, { status }, { headers: getHeaders() });
    setRequests(p => p.map(r => r.id === id ? { ...r, status } : r));
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/api/admin/quote-requests/${id}`, { headers: getHeaders() });
    setRequests(p => p.filter(r => r.id !== id));
    setDeleteConfirm(null);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="quote-requests-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Quote Requests</h1>
        <p className="text-sm text-slate-400 mt-1">Manage incoming quote requests from products</p>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
          <FileText className="w-12 h-12 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-400 text-sm">No quote requests yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(r => (
            <div key={r.id} data-testid={`quote-row-${r.id}`} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${r.status === 'new' ? 'bg-blue-50 text-blue-600' : r.status === 'contacted' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    {r.status === 'new' ? 'New' : r.status === 'contacted' ? 'Contacted' : r.status}
                  </div>
                  <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className="flex gap-2">
                  {r.status === 'new' && (
                    <button onClick={() => updateStatus(r.id, 'contacted')} className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100">
                      <CheckCircle className="w-3.5 h-3.5" /> Contacted
                    </button>
                  )}
                  <button onClick={() => setDeleteConfirm(r.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <p className="font-semibold text-slate-800 mb-2">{r.name}</p>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                {r.company && <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-300" />{r.company}</span>}
                {r.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-300" />{r.phone}</span>}
                {r.email && <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-slate-300" />{r.email}</span>}
                {r.city && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-300" />{r.city}</span>}
              </div>
              {r.product && <p className="text-sm"><span className="text-slate-400">Product:</span> <span className="text-[#00B4D8] font-medium">{r.product}</span></p>}
              {r.description && <p className="text-sm text-slate-500 mt-2 bg-slate-50 rounded-lg p-3">{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-2">Delete Request?</h3>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteRequestsPage;
